import { useMemo } from "react";
import { useParams } from "react-router-dom";

import { useUserToken } from "@/context/auth-context";
import { Entity, EntityType, getEntityNumericId } from "@/queries";
import { useInfinitePromptLabels } from "@/queries/prompt-labels";
import { useGetToolRegistry } from "@/queries/tool-registry";
import useGetWorkflowReleaseLabels from "@/queries/workflows/useWorkflowLabels";

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {};

export interface ReleaseLabel {
  id: number;
  name: string;
  created_at: string;
}

interface UseReleaseLabelsResult {
  labels: ReleaseLabel[];
  isLoading: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
}

const useReleaseLabels = (entity: Entity): UseReleaseLabelsResult => {
  const userToken = useUserToken();
  const { workspaceId } = useParams();

  const isPromptOrSnippet = entity.type === EntityType.PROMPT || entity.type === EntityType.SNIPPET;
  const isTool = entity.type === EntityType.TOOL;

  const numericEntityId = getEntityNumericId(entity.id);

  const promptLabelsQuery = useInfinitePromptLabels({
    userToken,
    workspace_id: Number(workspaceId),
    prompt_id: numericEntityId,
    per_page: 50,
    enabled: isPromptOrSnippet && numericEntityId !== undefined,
  });

  const workflowLabelsQuery = useGetWorkflowReleaseLabels(
    entity.type === EntityType.WORKFLOW ? numericEntityId : undefined,
    entity.type === EntityType.WORKFLOW && numericEntityId !== undefined,
  );

  const toolQuery = useGetToolRegistry(
    isTool ? Number(workspaceId) : null,
    isTool ? (numericEntityId ?? null) : null,
  );

  const labels = useMemo(() => {
    const sortByDate = (a: ReleaseLabel, b: ReleaseLabel) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime();

    if (isPromptOrSnippet) {
      return (
        promptLabelsQuery.data?.pages.flatMap(page =>
          page.prompt_labels.map(label => ({
            id: label.id,
            name: label.name,
            created_at: label.created_at,
          })),
        ) ?? []
      ).sort(sortByDate);
    }

    if (isTool) {
      return (
        toolQuery.data?.labels?.map(label => ({
          id: label.id,
          name: label.name,
          created_at: label.created_at,
        })) ?? []
      ).sort(sortByDate);
    }

    return (
      workflowLabelsQuery.data?.static_labels.map(label => ({
        id: label.id,
        name: label.name,
        created_at: label.created_at || new Date().toISOString(),
      })) ?? []
    ).sort(sortByDate);
  }, [
    isPromptOrSnippet,
    isTool,
    promptLabelsQuery.data?.pages,
    toolQuery.data?.labels,
    workflowLabelsQuery.data?.static_labels,
  ]);

  const getIsLoading = () => {
    if (isPromptOrSnippet) {
      return promptLabelsQuery.isLoading;
    }
    if (isTool) {
      return toolQuery.isLoading;
    }
    return workflowLabelsQuery.isLoading;
  };
  const isLoading = getIsLoading();

  return {
    labels,
    isLoading,
    hasNextPage: isPromptOrSnippet ? (promptLabelsQuery.hasNextPage ?? false) : false,
    isFetchingNextPage: isPromptOrSnippet ? promptLabelsQuery.isFetchingNextPage : false,
    fetchNextPage: isPromptOrSnippet ? promptLabelsQuery.fetchNextPage : noop,
  };
};

export default useReleaseLabels;
